import {
  AdditionalLinkType,
  DashboardFederationBoardMember,
  DashboardFederationClubMember,
  DashboardFederationDetail,
  DashboardFederationListItem,
  DashboardFederationManagerRef,
  FederationAdditionalLink,
  FederationAdditionalLinkInput,
  FederationMemberRole,
  FederationRef,
  FederationSocialLink,
  FederationSocialLinkInput,
  FederationStatus,
  GeographicScope,
  LegalEntityType as ApiLegalEntityType,
  SocialMediaType,
} from "@/api/__generated__/schemas";
import {
  FederationFragment,
  FederationWithFeesFragment,
  FederationWithPlatformsFragment,
  FederationWithManagersFragment,
  fromApiDate,
  OrganizationSubscriptionFragment,
  OrganizationType as ApiOrganizationType,
  GeographicScope as ApiOrganizationGeoScope,
  OrganizationMemberRole as ApiOrganizationMemberRole,
  BoardMemberFragment,
  FederationClubMemberFragment,
  FederationWithLegalDetailsFragment,
  LegalEntityType,
  FederationWithPaymentDetailsFragment,
  FederationWithBoardMembersFragment,
  FederationWithClubMembersFragment,
  FederationWithDocumentsFragment,
} from "@/graphql";
import { mapToBaseClub, mapToClubReference } from "@/mappers/club.mappers";
import { mapAvatarFields, mapToCountryCodeNullable } from "@/mappers/common.mappers";
import {
  mapToTaggedContentSettings,
  WithTaggedContentSettings,
} from "@/mappers/contentLink.mappers";
import {
  mapToBasePlatform,
  mapToPlatform,
  mapToPlatformReference,
} from "@/mappers/platform.mappers";
import { mapToSport, mapToSportRef } from "@/mappers/sport.mappers";
import { mapToUser } from "@/mappers/user.mappers";
import { OrganizationClubMember } from "@/types";
import {
  AdditionalLinkType as DomainAdditionalLinkType,
  SocialMediaType as DomainSocialMediaType,
} from "@/types/links.types";
import {
  Organization,
  OrganizationWithPlatforms,
  OrganizationWithManagers,
  OrganizationStatus,
  OrganizationType,
  OrganizationPlatformRelation,
  OrganizationReference,
  OrganizationTypeValues,
  OrganizationWithFees,
  OrganizationGeoScope,
  LegalEntityRole,
  BoardMember,
  OrganizationWithLegalDetails,
  OrganizationWithPaymentDetails,
  OrganizationWithBoardMembers,
  OrganizationWithClubMembers,
  OrganizationWithDocuments,
  OrganizationWithLinks,
  OrganizationAdditionalLink,
  OrganizationSocialLink,
  OrganizationListItem,
  OrganizationManager,
} from "@/types/organization.types";
import { User } from "@/types/user.types";

import { mapToDocumentsAndFolders, mapToDocumentsAndFoldersFromApi } from "./document.mappers";
import { mapToFeesSegment, mapToTournatedFeeVat } from "./payment.mappers";

export const mapToOrganization = (fragment: FederationFragment): Organization => {
  const type = fragment.organizationType
    ? mapToOrganizationType(fragment.organizationType)
    : "regionalGoverningBody";

  const geoScope: OrganizationGeoScope =
    type === "regionalGoverningBody"
      ? "local"
      : type === "internationalGoverningBody"
        ? "international"
        : type === "nationalGoverningBody"
          ? "national"
          : fragment.geographicScope
            ? mapToOrganizationGeoScope(fragment.geographicScope)
            : "local";

  return {
    id: String(fragment.id),
    name: fragment.organizationName ?? "",
    abbreviation: fragment.organizationAbbreviation!,
    about: !fragment.about || fragment.about === "NULL" ? null : fragment.about,
    educationalInstitutionNumber: null,
    geoScope,
    contactEmail: fragment.contactEmail ?? null,
    contactPhone: fragment.contactPhone ?? null,
    website: fragment.website ?? null,
    legacyLogoUrl: fragment.logo ?? null,
    country: fragment.country ? mapToCountryCodeNullable(fragment.country) : null,
    status: fragment.status ? mapToOrganizationStatus(fragment.status) : "active",
    enableLicenses: fragment.enableLicenses ?? false,
    enableCertificates: fragment.enableCertificates ?? false,
    enableApplicationForms: fragment.enableApplicationForms ?? false,
    enableRankings: fragment.enableRankings ?? false,
    // The GraphQL Federation type does not expose isClubForTeamRequired or the
    // tagged-content settings; the org read path now goes through REST
    // (mapToOrganizationFromApi). Default them here.
    isClubForTeamRequired: false,
    ...mapToTaggedContentSettings({}),
    createdAt: fromApiDate(fragment.createdAt),
    updatedAt: fromApiDate(fragment.updatedAt),
    types: type ? [type] : [],
    sports: fragment.sports?.map(mapToSport) ?? [],
    logoFile: fragment.logoFile ?? null,
  };
};

/**
 * Adapts the legacy GraphQL `User` (flat `avatar` URL) to the slim
 * `OrganizationManager`, surfacing the legacy URL via `legacyFileUrl` so
 * `resolveAvatarUrl` still resolves it at render.
 */
const mapToOrganizationManagerFromUser = (user: User): OrganizationManager => ({
  id: user.id,
  name: user.name ?? "",
  surname: user.surname ?? "",
  email: user.email ?? "",
  phone: user.phone ?? null,
  contactEmail: null,
  contactPhone: null,
  avatarFile: null,
  legacyFileUrl: user.avatar ?? null,
});

const MOCK_MAIN_MANAGER: OrganizationManager = {
  id: "0",
  name: "Mock",
  surname: "Manager",
  email: "mock.manager@example.com",
  avatarFile: null,
  legacyFileUrl: null,
  phone: null,
  contactEmail: null,
  contactPhone: null,
};

export const mapToOrganizationWithUser = (
  fragment: FederationWithManagersFragment
): OrganizationWithManagers => ({
  mainManager: fragment.user
    ? mapToOrganizationManagerFromUser(mapToUser(fragment.user))
    : MOCK_MAIN_MANAGER,
  mainManagerContactEmail: null,
  mainManagerContactPhone: null,
  managers: [],
  // managers: fragment.users?.map(mapToUser) ?? [],
});

export const mapToOrganizationWithPlatforms = (
  fragment: FederationWithPlatformsFragment
): OrganizationWithPlatforms => ({
  platforms: fragment.platforms?.map((item) => mapToBasePlatform(item.platform!)) ?? [],
});

export const mapToOrganizationWithLegalDetails = (
  fragment: FederationWithLegalDetailsFragment
): OrganizationWithLegalDetails => {
  if (fragment.legalEntityType === LegalEntityType.Individual) {
    return {
      legalDetails: {
        type: "individual",
        email: fragment.legalEmail ?? "",
        name: fragment.legalName ?? "",
        surname: fragment.legalSurname ?? "",
        residence: fragment.legalCountry ? mapToCountryCodeNullable(fragment.legalCountry) : null,
        personalNumber: fragment.personalIdentificationNumber ?? "",
        phone: fragment.legalPhone ?? "",
      },
    };
  }

  return {
    legalDetails: {
      type: "company",
      companyName: fragment.legalCompanyName ?? "",
      email: fragment.legalEmail ?? "",
      registrationNumber: fragment.registrationNumber ?? "",
      registrationCountry: fragment.legalCountry
        ? mapToCountryCodeNullable(fragment.legalCountry)
        : null,
      vatNumber: fragment.vatNumber ?? "",
      address: fragment.legalAddress ?? "",
      signerName: fragment.signerName ?? "",
      signerSurname: fragment.signerSurname ?? "",
      signerRole: fragment.signerRole ?? "",
      signerEmail: fragment.signerEmail ?? "",
    },
  };
};

export const mapToOrganizationWithPaymentDetails = (
  fragment: FederationWithPaymentDetailsFragment
): OrganizationWithPaymentDetails => {
  if (fragment.legalEntityType === LegalEntityType.Individual) {
    return {
      paymentDetails: {
        bankTransferDetails: {
          type: "individual",
          accountHolderName: fragment.bankAccountHolder ?? "",
          iban: fragment.bankIban ?? "",
          enabled: fragment.bankEnabled ?? false,
          country: fragment.bankCountry
            ? (mapToCountryCodeNullable(fragment.bankCountry) ?? "LV")
            : "LV",
          city: fragment.bankCity ?? "",
          address: fragment.bankAddress ?? "",
          postalCode: fragment.bankPostalCode ?? "",
        },
      },
    };
  }

  return {
    paymentDetails: {
      bankTransferDetails: {
        type: "company",
        enabled: fragment.bankEnabled ?? false,
        country: fragment.bankCountry
          ? (mapToCountryCodeNullable(fragment.bankCountry) ?? "LV")
          : "LV",
        city: fragment.bankCity ?? "",
        address: fragment.bankAddress ?? "",
        postalCode: fragment.bankPostalCode ?? "",
        accountHolderName: fragment.bankAccountHolder ?? "",
        companyRegistrationNumber: fragment.bankCompanyRegistrationNumber ?? "",
        vatNumber: fragment.bankVatNumber ?? "",
        bankName: fragment.bankName ?? "",
        iban: fragment.bankIban ?? "",
      },
    },
  };
};

export const mapToOrganizationWithBoardMembers = (
  fragment: FederationWithBoardMembersFragment
): OrganizationWithBoardMembers => ({
  boardMembers: fragment.boardMembers?.map(mapToBoardMember) ?? [],
});

export const mapToOrganizationWithDocuments = (
  fragment: FederationWithDocumentsFragment
): OrganizationWithDocuments => mapToDocumentsAndFolders(fragment);

export const mapToOrganizationWithClubMembers = (
  fragment: FederationWithClubMembersFragment
): OrganizationWithClubMembers => ({
  clubMembers: fragment.clubMembers?.map(mapToOrganizationClubMember) ?? [],
});

export const mapToOrganizationStatus = (status: string): OrganizationStatus => {
  switch (status.toLowerCase()) {
    case "active":
      return "active";
    case "inactive":
      return "inactive";
    default:
      return "active";
  }
};

export const mapToOrganizationType = (apiType: string): OrganizationType => {
  if (OrganizationTypeValues.includes(apiType as OrganizationType)) {
    return apiType as OrganizationType;
  }

  switch (apiType.toLowerCase()) {
    case "community":
      return "community";
    case "local organization":
    case "local_organization":
    case "localorganization":
      return "regionalGoverningBody";
    case "international organization":
    case "international_organization":
    case "internationalorganization":
      return "internationalGoverningBody";
    case "national organization":
    case "nationalorganization":
    case "nation_organization":
      return "nationalGoverningBody";
    case "regionalgoverningbody":
    case "regional_governing_body":
      return "regionalGoverningBody";
    case "international_governing_body":
      return "internationalGoverningBody";
    case "internationalgoverningbody":
    case "national_governing_body":
    case "nationalgoverningbody":
      return "nationalGoverningBody";
    case "umbrella_sports_body":
    case "umbrellasportsbody":
      return "umbrellaSportsBody";
    case "governmentauthority":
    case "government_authority":
      return "governmentAuthority";
    case "municipality":
      return "municipality";
    case "clubowner":
    case "club_owner":
      return "clubOwner";
    case "clubchainowner":
    case "club_chain_owner":
      return "clubChainOwner";
    case "privateentity":
    case "private_entity":
      return "privateEntity";
    case "sports_club":
    case "sportsclub":
      return "sportsClub";
    case "school":
      return "school";
    case "union":
      return "union";
    case "sports_ministry":
    case "sportsministry":
      return "sportsMinistry";
    default:
      throw new Error(`Unexpected organization type: ${apiType}`);
  }
};

export const mapToOrganizationWithFees = (
  fragment: FederationWithFeesFragment
): OrganizationWithFees => ({
  // The legacy GraphQL fragment carries no entity VAT; every platform inherits.
  customFeeVat: {},
  customFees: Object.fromEntries(
    fragment.platforms?.map((item) => {
      const feeSegments = item.customFee?.map(mapToFeesSegment) ?? [];
      const entryFeesArray = feeSegments.filter((fee) => fee.serviceType === "entry");
      const licenseFeesArray = feeSegments.filter((fee) => fee.serviceType === "license");

      const entryFees = entryFeesArray.length > 0 ? entryFeesArray : null;
      const licenseFees = licenseFeesArray.length > 0 ? licenseFeesArray : null;

      return [
        String(item.platform?.id ?? ""),
        entryFees || licenseFees
          ? {
              entry: entryFeesArray,
              license: licenseFeesArray,
            }
          : null,
      ];
    }) ?? []
  ),
});

export const mapToApiOrganizationType = (
  organizationType: OrganizationType
): ApiOrganizationType => {
  switch (organizationType) {
    case "regionalGoverningBody":
      return ApiOrganizationType.RegionalGoverningBody;
    case "internationalGoverningBody":
      return ApiOrganizationType.InternationalGoverningBody;
    case "nationalGoverningBody":
      return ApiOrganizationType.NationalGoverningBody;
    case "umbrellaSportsBody":
      return ApiOrganizationType.UmbrellaSportsBody;
    case "governmentAuthority":
      return ApiOrganizationType.GovernmentAuthority;
    case "municipality":
      return ApiOrganizationType.Municipality;
    case "clubOwner":
      return ApiOrganizationType.ClubOwner;
    case "clubChainOwner":
      return ApiOrganizationType.ClubChainOwner;
    case "privateEntity":
      return ApiOrganizationType.PrivateEntity;
    case "sportsClub":
      return ApiOrganizationType.SportsClub;
    case "school":
      return ApiOrganizationType.School;
    case "union":
      return ApiOrganizationType.Union;
    case "sportsMinistry":
      return ApiOrganizationType.SportsMinistry;
    case "community":
      return ApiOrganizationType.Community;
    case "educationalInstitution":
      return ApiOrganizationType.EducationalInstitution;
  }
};

export const mapToOrganizationPlatformRelation = (
  fragment: OrganizationSubscriptionFragment
): OrganizationPlatformRelation => ({
  id: fragment.id,
  federation: fragment.federation ? mapToOrganization(fragment.federation) : null,
  platform: fragment.platform ? mapToPlatform(fragment.platform) : null,
});

export const mapToOrganizationApiStatus = (status: OrganizationStatus): string => {
  switch (status) {
    case "active":
      return "Active";
    case "inactive":
      return "Inactive";
  }
};

export const mapToApiOrganizationGeoScope = (
  geoScope: OrganizationGeoScope
): ApiOrganizationGeoScope => {
  switch (geoScope) {
    case "international":
      return ApiOrganizationGeoScope.International;
    case "national":
      return ApiOrganizationGeoScope.National;
    case "local":
      return ApiOrganizationGeoScope.Local;
  }
};

export const mapToApiOrganizationMemberRole = (
  role: LegalEntityRole
): ApiOrganizationMemberRole => {
  switch (role) {
    case "president":
      return ApiOrganizationMemberRole.President;
    case "vicePresident":
      return ApiOrganizationMemberRole.VicePresident;
    case "boardMember":
      return ApiOrganizationMemberRole.BoardMember;
    case "secretaryGeneral":
      return ApiOrganizationMemberRole.SecretaryGeneral;
    case "chiefExecutiveOfficer":
      return ApiOrganizationMemberRole.ChiefExecutiveOfficer;
    case "treasurer":
      return ApiOrganizationMemberRole.Treasurer;
    case "executiveDirector":
      return ApiOrganizationMemberRole.ExecutiveDirector;
    case "technicalDirector":
      return ApiOrganizationMemberRole.TechnicalDirector;
    case "operationsManager":
      return ApiOrganizationMemberRole.OperationsManager;
    case "competitionManager":
      return ApiOrganizationMemberRole.CompetitionManager;
    case "committeeChair":
      return ApiOrganizationMemberRole.CommitteeChair;
    case "committeeMember":
      return ApiOrganizationMemberRole.CommitteeMember;
    case "athleteRepresentative":
      return ApiOrganizationMemberRole.AthleteRepresentative;
    case "clubRepresentative":
      return ApiOrganizationMemberRole.ClubRepresentative;
    case "coachingManager":
      return ApiOrganizationMemberRole.CoachingManager;
    case "generalAssemblyMember":
      return ApiOrganizationMemberRole.GeneralAssemblyMember;
    case "officiatingManager":
      return ApiOrganizationMemberRole.OfficiatingManager;
    case "votingMember":
      return ApiOrganizationMemberRole.VotingMember;
  }
};

export const mapToLegalEntityRole = (role: ApiOrganizationMemberRole): LegalEntityRole => {
  switch (role) {
    case ApiOrganizationMemberRole.President:
      return "president";
    case ApiOrganizationMemberRole.VicePresident:
      return "vicePresident";
    case ApiOrganizationMemberRole.BoardMember:
      return "boardMember";
    case ApiOrganizationMemberRole.SecretaryGeneral:
      return "secretaryGeneral";
    case ApiOrganizationMemberRole.ChiefExecutiveOfficer:
      return "chiefExecutiveOfficer";
    case ApiOrganizationMemberRole.Treasurer:
      return "treasurer";
    case ApiOrganizationMemberRole.ExecutiveDirector:
      return "executiveDirector";
    case ApiOrganizationMemberRole.TechnicalDirector:
      return "technicalDirector";
    case ApiOrganizationMemberRole.OperationsManager:
      return "operationsManager";
    case ApiOrganizationMemberRole.CompetitionManager:
      return "competitionManager";
    case ApiOrganizationMemberRole.CommitteeChair:
      return "committeeChair";
    case ApiOrganizationMemberRole.CommitteeMember:
      return "committeeMember";
    case ApiOrganizationMemberRole.AthleteRepresentative:
      return "athleteRepresentative";
    case ApiOrganizationMemberRole.ClubRepresentative:
      return "clubRepresentative";
    case ApiOrganizationMemberRole.GeneralAssemblyMember:
      return "generalAssemblyMember";
    case ApiOrganizationMemberRole.VotingMember:
      return "votingMember";
    case ApiOrganizationMemberRole.OfficiatingManager:
      return "officiatingManager";
    case ApiOrganizationMemberRole.CoachingManager:
      return "coachingManager";
  }
};

export const mapToBoardMember = (fragment: BoardMemberFragment): BoardMember => ({
  id: fragment.id,
  name: fragment.name,
  surname: fragment.surname,
  role: mapToLegalEntityRole(fragment.role),
  email: fragment.email,
  phone: fragment.phone ?? null,
  logoUrl:
    fragment.logo && fragment.logoFileId
      ? {
          url: fragment.logo,
          fileId: fragment.logoFileId,
        }
      : null,
});

export const mapToOrganizationClubMember = (
  fragment: FederationClubMemberFragment
): OrganizationClubMember => ({
  id: fragment.id,
  title: fragment.title,
  contactName: fragment.contactName ?? "",
  contactSurname: fragment.contactSurname,
  contactEmail: fragment.contactEmail,
  contactPhone: fragment.contactPhone ?? null,
  contactRole: mapToLegalEntityRole(fragment.contactRole),
  logoUrl: fragment.logo ?? null,
  registrationNumber: fragment.registrationNumber ?? "",
  website: fragment.website ?? null,
  country: fragment.country ? mapToCountryCodeNullable(fragment.country) : null,
  city: fragment.city ?? "",
  address: fragment.address ?? "",
  postalCode: fragment.postalCode ?? "",
  club: fragment.club ? mapToBaseClub(fragment.club) : null,
});

export const mapToOrganizationGeoScope = (
  geoScope: ApiOrganizationGeoScope
): OrganizationGeoScope => {
  switch (geoScope) {
    case ApiOrganizationGeoScope.International:
      return "international";
    case ApiOrganizationGeoScope.National:
      return "national";
    case ApiOrganizationGeoScope.Local:
      return "local";
  }
};

export const mapToOrganizationReference = (input: FederationRef): OrganizationReference => ({
  id: input.id,
  name: input.organizationName ?? "",
  abbreviation: input.organizationAbbreviation ?? null,
  logo: input.logo ?? null,
});

const API_STATUS_TO_DOMAIN: { [K in FederationStatus]: OrganizationStatus } = {
  [FederationStatus.Active]: "active",
  [FederationStatus.Inactive]: "inactive",
};

export const mapToOrganizationStatusFromApi = (status: FederationStatus): OrganizationStatus =>
  API_STATUS_TO_DOMAIN[status];

const API_GEO_SCOPE_TO_DOMAIN: { [K in GeographicScope]: OrganizationGeoScope } = {
  [GeographicScope.local]: "local",
  [GeographicScope.national]: "national",
  [GeographicScope.international]: "international",
};

export const mapToOrganizationGeoScopeFromApi = (scope: GeographicScope): OrganizationGeoScope =>
  API_GEO_SCOPE_TO_DOMAIN[scope];

/**
 * Builds the slim domain `OrganizationManager` from the manager ref the federation
 * REST endpoints return. Only the contact fields are known.
 */
export const mapToOrganizationManager = (
  ref: DashboardFederationManagerRef
): OrganizationManager => ({
  id: ref.id,
  name: ref.name ?? "",
  surname: ref.surname ?? "",
  email: ref.email ?? "",
  phone: ref.phone ?? null,
  contactEmail: ref.contactEmail ?? null,
  contactPhone: ref.contactPhone ?? null,
  ...mapAvatarFields(ref),
});

const UNKNOWN_MANAGER: OrganizationManager = mapToOrganizationManager({
  id: "0",
  name: "Unknown",
  surname: "manager",
  email: null,
  phone: null,
  contactEmail: null,
  contactPhone: null,
  avatarFile: null,
  legacyAvatarUrl: null,
});

export const mapToOrganizationListItem = (
  item: DashboardFederationListItem
): OrganizationListItem => ({
  id: item.id,
  internalId: item.internalId,
  name: item.name ?? "",
  abbreviation: item.abbreviation ?? "",
  about: item.about,
  educationalInstitutionNumber: null,
  types: item.types.filter((type): type is OrganizationType =>
    OrganizationTypeValues.includes(type as OrganizationType)
  ),
  geoScope: item.geographicScope ? mapToOrganizationGeoScopeFromApi(item.geographicScope) : "local",
  contactEmail: item.contactEmail ?? null,
  contactPhone: item.contactPhone ?? null,
  mainManagerContactEmail: item.mainManagerContactEmail ?? null,
  mainManagerContactPhone: item.mainManagerContactPhone ?? null,
  website: item.website ?? null,
  legacyLogoUrl: item.logo ?? null,
  logoFile: null,
  country: item.country ? mapToCountryCodeNullable(item.country) : null,
  status: item.status ? mapToOrganizationStatusFromApi(item.status) : "active",
  enableLicenses: item.enableLicenses,
  enableCertificates: item.enableCertificates,
  enableApplicationForms: item.enableApplicationForms,
  enableRankings: item.enableRankings,
  // TODO(DEV-2153): the REST DashboardFederationListItem DTO does not yet expose
  // isClubForTeamRequired. Read it from `item` once the backend adds the field.
  isClubForTeamRequired: false,
  // TODO(DEV-2154): DashboardFederationListItem does not expose the tagged-content
  // settings; the list UI never renders them. Read from `item` once added.
  ...mapToTaggedContentSettings({}),
  createdAt: new Date(item.createdAt),
  updatedAt: new Date(item.updatedAt),
  sports: item.sports.map(mapToSportRef),
  platforms: item.platforms.map(mapToPlatformReference),
  mainManager: item.mainManager ? mapToOrganizationManager(item.mainManager) : UNKNOWN_MANAGER,
  managers: [],
});

const API_MEMBER_ROLE_TO_DOMAIN: { [K in FederationMemberRole]: LegalEntityRole } = {
  [FederationMemberRole.president]: "president",
  [FederationMemberRole.vicePresident]: "vicePresident",
  [FederationMemberRole.boardMember]: "boardMember",
  [FederationMemberRole.secretaryGeneral]: "secretaryGeneral",
  [FederationMemberRole.chiefExecutiveOfficer]: "chiefExecutiveOfficer",
  [FederationMemberRole.treasurer]: "treasurer",
  [FederationMemberRole.executiveDirector]: "executiveDirector",
  [FederationMemberRole.technicalDirector]: "technicalDirector",
  [FederationMemberRole.operationsManager]: "operationsManager",
  [FederationMemberRole.competitionManager]: "competitionManager",
  [FederationMemberRole.committeeChair]: "committeeChair",
  [FederationMemberRole.committeeMember]: "committeeMember",
  [FederationMemberRole.athleteRepresentative]: "athleteRepresentative",
  [FederationMemberRole.clubRepresentative]: "clubRepresentative",
  [FederationMemberRole.generalAssemblyMember]: "generalAssemblyMember",
  [FederationMemberRole.votingMember]: "votingMember",
  [FederationMemberRole.officiatingManager]: "officiatingManager",
  [FederationMemberRole.coachingManager]: "coachingManager",
};

/** Map a federation REST member-role enum value into the local domain role. */
export const mapToLegalEntityRoleFromApi = (role: FederationMemberRole): LegalEntityRole =>
  API_MEMBER_ROLE_TO_DOMAIN[role];

const DOMAIN_ROLE_TO_API_MEMBER_ROLE: { [K in LegalEntityRole]: FederationMemberRole } = {
  president: FederationMemberRole.president,
  vicePresident: FederationMemberRole.vicePresident,
  boardMember: FederationMemberRole.boardMember,
  secretaryGeneral: FederationMemberRole.secretaryGeneral,
  chiefExecutiveOfficer: FederationMemberRole.chiefExecutiveOfficer,
  treasurer: FederationMemberRole.treasurer,
  executiveDirector: FederationMemberRole.executiveDirector,
  technicalDirector: FederationMemberRole.technicalDirector,
  operationsManager: FederationMemberRole.operationsManager,
  competitionManager: FederationMemberRole.competitionManager,
  committeeChair: FederationMemberRole.committeeChair,
  committeeMember: FederationMemberRole.committeeMember,
  athleteRepresentative: FederationMemberRole.athleteRepresentative,
  clubRepresentative: FederationMemberRole.clubRepresentative,
  generalAssemblyMember: FederationMemberRole.generalAssemblyMember,
  votingMember: FederationMemberRole.votingMember,
  officiatingManager: FederationMemberRole.officiatingManager,
  coachingManager: FederationMemberRole.coachingManager,
};

/** Local domain `LegalEntityRole` → REST `FederationMemberRole`. */
export const mapToApiFederationMemberRole = (role: LegalEntityRole): FederationMemberRole =>
  DOMAIN_ROLE_TO_API_MEMBER_ROLE[role];

/**
 * The checked-in Orval client predates the per-kind tagged-content settings —
 * see `WithTaggedContentSettings` in `contentLink.mappers.ts` for the single
 * contract assumption and its `TODO(codegen)`.
 */
type DashboardFederationDetailContract = WithTaggedContentSettings<DashboardFederationDetail>;

export const mapToOrganizationFromApi = (
  detail: DashboardFederationDetailContract
): Organization => ({
  id: detail.id,
  name: detail.name ?? "",
  abbreviation: detail.abbreviation ?? "",
  about: !detail.about || detail.about === "NULL" ? null : detail.about,
  educationalInstitutionNumber: detail.educationalInstitutionNumber,
  types: detail.types.filter((type): type is OrganizationType =>
    OrganizationTypeValues.includes(type as OrganizationType)
  ),
  geoScope: detail.geographicScope
    ? mapToOrganizationGeoScopeFromApi(detail.geographicScope)
    : "local",
  contactEmail: detail.contactEmail ?? null,
  contactPhone: detail.contactPhone ?? null,
  website: detail.website ?? null,
  legacyLogoUrl: detail.logo ?? null,
  // Seed the file-backed logo (url + storage id) so the general-tab logo field
  // carries a `savedFileId`. Without it, an unchanged logo has no id and every
  // save re-downloads + re-uploads the existing file (see useGetFileUploadFieldApiValue).
  logoFile: detail.logo && detail.logoFileId ? { id: detail.logoFileId, url: detail.logo } : null,
  country: detail.country ? mapToCountryCodeNullable(detail.country) : null,
  status: detail.status ? mapToOrganizationStatusFromApi(detail.status) : "active",
  enableLicenses: detail.enableLicenses,
  enableCertificates: detail.enableCertificates,
  enableApplicationForms: detail.enableApplicationForms,
  enableRankings: detail.enableRankings,
  // TODO(DEV-2153): the REST DashboardFederationDetail DTO does not yet expose
  // isClubForTeamRequired. Read it from `detail` once the backend adds the field
  // (re-run `yarn rest:codegen`).
  isClubForTeamRequired: false,
  ...mapToTaggedContentSettings(detail),
  createdAt: new Date(detail.createdAt),
  updatedAt: new Date(detail.updatedAt),
  sports: detail.sports.map(mapToSportRef),
});

export const mapToOrganizationWithUserFromApi = (
  detail: DashboardFederationDetail
): OrganizationWithManagers => ({
  mainManager: detail.mainManager ? mapToOrganizationManager(detail.mainManager) : UNKNOWN_MANAGER,
  mainManagerContactEmail: detail.mainManagerContactEmail,
  mainManagerContactPhone: detail.mainManagerContactPhone,
  managers: detail.managers.map((ref: DashboardFederationManagerRef) =>
    mapToOrganizationManager(ref)
  ),
});

export const mapToOrganizationWithLegalDetailsFromApi = (
  detail: DashboardFederationDetail
): OrganizationWithLegalDetails => {
  if (detail.legalEntityType === ApiLegalEntityType.individual) {
    return {
      legalDetails: {
        type: "individual",
        email: detail.legalEmail ?? "",
        name: detail.legalName ?? "",
        surname: detail.legalSurname ?? "",
        residence: detail.legalCountry ? mapToCountryCodeNullable(detail.legalCountry) : null,
        personalNumber: detail.personalIdentificationNumber ?? "",
        phone: detail.legalPhone ?? "",
      },
    };
  }

  return {
    legalDetails: {
      type: "company",
      companyName: detail.legalCompanyName ?? "",
      email: detail.legalEmail ?? "",
      registrationNumber: detail.registrationNumber ?? "",
      registrationCountry: detail.legalCountry
        ? mapToCountryCodeNullable(detail.legalCountry)
        : null,
      vatNumber: detail.vatNumber ?? "",
      address: detail.legalAddress ?? "",
      signerName: detail.signerName ?? "",
      signerSurname: detail.signerSurname ?? "",
      signerRole: detail.signerRole ?? "",
      signerEmail: detail.signerEmail ?? "",
    },
  };
};

export const mapToOrganizationWithPaymentDetailsFromApi = (
  detail: DashboardFederationDetail
): OrganizationWithPaymentDetails => {
  if (detail.legalEntityType === ApiLegalEntityType.individual) {
    return {
      paymentDetails: {
        bankTransferDetails: {
          type: "individual",
          accountHolderName: detail.bankAccountHolder ?? "",
          iban: detail.bankIban ?? "",
          enabled: detail.bankEnabled,
          country: detail.bankCountry
            ? (mapToCountryCodeNullable(detail.bankCountry) ?? "LV")
            : "LV",
          city: detail.bankCity ?? "",
          address: detail.bankAddress ?? "",
          postalCode: detail.bankPostalCode ?? "",
        },
      },
    };
  }

  return {
    paymentDetails: {
      bankTransferDetails: {
        type: "company",
        enabled: detail.bankEnabled,
        country: detail.bankCountry ? (mapToCountryCodeNullable(detail.bankCountry) ?? "LV") : "LV",
        city: detail.bankCity ?? "",
        address: detail.bankAddress ?? "",
        postalCode: detail.bankPostalCode ?? "",
        accountHolderName: detail.bankAccountHolder ?? "",
        companyRegistrationNumber: detail.bankCompanyRegistrationNumber ?? "",
        vatNumber: detail.bankVatNumber ?? "",
        bankName: detail.bankName ?? "",
        iban: detail.bankIban ?? "",
      },
    },
  };
};

const mapToBoardMemberFromApi = (item: DashboardFederationBoardMember): BoardMember => ({
  id: item.id,
  name: item.name ?? "",
  surname: item.surname ?? "",
  role: item.role ? mapToLegalEntityRoleFromApi(item.role) : "boardMember",
  email: item.email ?? "",
  phone: item.phone ?? null,
  logoUrl: item.logo && item.logoFileId ? { url: item.logo, fileId: item.logoFileId } : null,
});

export const mapToOrganizationWithBoardMembersFromApi = (
  detail: DashboardFederationDetail
): OrganizationWithBoardMembers => ({
  boardMembers: detail.boardMembers.map(mapToBoardMemberFromApi),
});

const mapToOrganizationClubMemberFromApi = (
  item: DashboardFederationClubMember
): OrganizationClubMember => ({
  id: item.id,
  title: item.title ?? "",
  contactName: item.contactName ?? "",
  contactSurname: item.contactSurname ?? "",
  contactEmail: item.contactEmail ?? "",
  contactPhone: item.contactPhone ?? null,
  contactRole: item.contactRole ? mapToLegalEntityRoleFromApi(item.contactRole) : "boardMember",
  logoUrl: item.logo ?? null,
  registrationNumber: item.registrationNumber ?? "",
  website: item.website ?? null,
  country: item.country ? mapToCountryCodeNullable(item.country) : null,
  city: item.city ?? "",
  address: item.address ?? "",
  postalCode: item.postalCode ?? "",
  club: item.club ? mapToClubReference(item.club) : null,
});

export const mapToOrganizationWithClubMembersFromApi = (
  detail: DashboardFederationDetail
): OrganizationWithClubMembers => ({
  clubMembers: detail.clubMembers.map(mapToOrganizationClubMemberFromApi),
});

const API_SOCIAL_MEDIA_TYPE_TO_DOMAIN: { [K in SocialMediaType]: DomainSocialMediaType } = {
  [SocialMediaType.instagram]: "instagram",
  [SocialMediaType.facebook]: "facebook",
  [SocialMediaType.tiktok]: "tiktok",
  [SocialMediaType.youtube]: "youtube",
  [SocialMediaType.linkedin]: "linkedin",
  [SocialMediaType.discord]: "discord",
  [SocialMediaType.twitch]: "twitch",
};

const mapToSocialMediaTypeFromApi = (type: SocialMediaType): DomainSocialMediaType =>
  API_SOCIAL_MEDIA_TYPE_TO_DOMAIN[type];

const mapToOrganizationSocialLinkFromApi = (
  link: FederationSocialLink
): OrganizationSocialLink => ({
  socialMediaType: mapToSocialMediaTypeFromApi(link.socialMediaType),
  url: link.url,
});

// The REST enum is snake_case on the wire; the domain type is camelCase.
const API_ADDITIONAL_LINK_TYPE_TO_DOMAIN: {
  [key in AdditionalLinkType]: DomainAdditionalLinkType;
} = {
  [AdditionalLinkType.european_federation]: "europeanFederation",
  [AdditionalLinkType.international_federation]: "internationalFederation",
  [AdditionalLinkType.other]: "other",
};

const DOMAIN_ADDITIONAL_LINK_TYPE_TO_API: {
  [key in DomainAdditionalLinkType]: AdditionalLinkType;
} = {
  europeanFederation: AdditionalLinkType.european_federation,
  internationalFederation: AdditionalLinkType.international_federation,
  other: AdditionalLinkType.other,
};

const mapToAdditionalLinkTypeFromApi = (type: AdditionalLinkType): DomainAdditionalLinkType =>
  API_ADDITIONAL_LINK_TYPE_TO_DOMAIN[type] ?? "other";

const mapToOrganizationAdditionalLinkFromApi = (
  link: FederationAdditionalLink
): OrganizationAdditionalLink => ({
  type: mapToAdditionalLinkTypeFromApi(link.type),
  label: { ...link.label },
  url: link.url,
});

export const mapToOrganizationWithLinksFromApi = (
  detail: DashboardFederationDetail
): OrganizationWithLinks => ({
  socialLinks: detail.socialLinks.map(mapToOrganizationSocialLinkFromApi),
  additionalLinks: detail.additionalLinks.map(mapToOrganizationAdditionalLinkFromApi),
});

export const mapToApiFederationSocialLinks = (
  links: OrganizationSocialLink[]
): FederationSocialLinkInput[] =>
  links.map((link) => ({
    socialMediaType: link.socialMediaType as SocialMediaType,
    url: link.url,
  }));

export const mapToApiFederationAdditionalLinks = (
  links: OrganizationAdditionalLink[]
): FederationAdditionalLinkInput[] =>
  links.map((link) => ({
    type: DOMAIN_ADDITIONAL_LINK_TYPE_TO_API[link.type],
    label: { ...link.label },
    url: link.url,
  }));

export const mapToOrganizationWithDocumentsFromApi = (
  detail: DashboardFederationDetail
): OrganizationWithDocuments =>
  mapToDocumentsAndFoldersFromApi({
    documents: detail.documents,
    documentFolders: detail.documentFolders,
  });

/**
 * Build the `OrganizationWithFees.customFees` map from the federation detail's slim
 * `customFees` array (REST `DashboardFederationDetail.customFees`). Each entry is a
 * `{ platformId, fees }` group whose `fees` are this federation's custom fee rows on
 * that platform (both entry + license, distinguished by each row's `serviceType`).
 *
 * Rows are split by `serviceType` and run through the existing read-back mapper
 * (`mapToFeesSegment`) to produce the form's `{ entry, license }` fee segments.
 * Platforms absent from the array are not custom (left out of the map → the form
 * falls back to platform/default mode).
 */
export const mapFederationCustomFeesToOrganizationFees = (
  customFees: DashboardFederationDetail["customFees"],
  /**
   * The detail's `customFeeVat` — only platforms where the entity configured its
   * own VAT on the Tournated fee; absent ones inherit the platform's.
   */
  customFeeVat: DashboardFederationDetail["customFeeVat"] | undefined
): OrganizationWithFees => ({
  customFeeVat: Object.fromEntries(
    // Optional-chained: a backend predating the projection reads as "inherits".
    (customFeeVat ?? []).map((vat) => [vat.platformId, mapToTournatedFeeVat(vat)])
  ),
  customFees: Object.fromEntries(
    customFees.map(({ platformId, fees }) => {
      const segments = fees.map((fee) => mapToFeesSegment(fee));
      return [
        platformId,
        {
          entry: segments.filter((s) => s.serviceType === "entry"),
          license: segments.filter((s) => s.serviceType === "license"),
        },
      ];
    })
  ),
});
